import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentFocusKey, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { routeStackManager } from '@/utils/routeStackManager';
import { useKeyboard } from './useKeyboard';
import { isBackKeyHandled } from './useFocusable';
import { backHandlerManager } from '@/utils/backHandlerManager';
import { isPlayPage, isPlayDetailPage, removeVidFromPath } from '@/utils/playRouteHelper';

/**
 * 全局回退导航 Hook - 优化版
 * 提供全局键盘监听和回退功能
 *
 * 架构设计：
 * 1. 页面级处理（优先级高）：
 *    - 使用 useFocusable 的 onBackPress 回调
 *    - 返回 true：阻止事件传播，页面内部已处理（如关闭子页面）
 *    - 返回 false 或 void：允许传播到全局处理器
 *    - 适用于：播放详情页（关闭剧集列表/简介详情）
 *    - 注意：onBackPress 只在焦点在当前元素上时触发
 *
 * 2. 全局级处理（优先级低）：
 *    - 当页面级未处理时（onBackPress 返回 false/void 或未触发），触发全局导航逻辑
 *    - 特殊处理：焦点在导航栏时，频道页和首页直接显示退出对话框
 *    - 监听路由变化，自动管理路由栈
 *    - 回退时，从栈中弹出当前路径，导航到上一个路径
 *    - 栈底时的特殊逻辑：
 *      * 频道页（url包含/channel/）：显示退出确认对话框
 *      * 根目录（/）：显示退出确认对话框
 *      * 其他页面：通过replace跳转到根目录
 *
 * 3. 路由栈管理规则：
 *    - 首页（/）作为落地页不入栈，确保回退时能正确触发退出弹窗
 *    - 其他页面正常入栈，回退时按栈顺序返回
 *
 * 兼容性保证：
 * - 页面可以通过 onBackPress 自定义返回行为
 * - 全局处理器不会干扰页面级的特殊逻辑
 * - 统一的退出对话框体验
 */
export const useBackNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showExitDialog, setShowExitDialog] = useState(false);
  const previousPathRef = useRef<string>(location.pathname);
  const isInitialMountRef = useRef<boolean>(true);
  const isNavigatingBackRef = useRef<boolean>(false); // 标记是否正在回退
  const lastFocusKeyRef = useRef<string | null>(null); // 保存打开弹窗前的焦点

  // 判断是否是频道页
  const isChannelPage = useCallback((path: string) => {
    return path.includes('/channel/');
  }, []);

  // 判断是否是首页（home）路由
  const isHomePage = useCallback((path: string) => {
    return path === '/';
  }, []);

  // 根据路径生成页面 ID
  const getPageIdFromPath = useCallback((path: string): string => {
    if (path.startsWith('/history')) {
      return 'history-page';
    }
    if (path.startsWith('/channel')) {
      return 'channel-page';
    }
    if (path.startsWith('/more')) {
      return 'filter-page';
    }
    if (path.startsWith('/search')) {
      return 'search-page';
    }
    if (path.startsWith('/playDetail')) {
      return 'play-detail-page';
    }
    if (path.startsWith('/play')) {
      return 'play-page';
    }
    if (path.startsWith('/account')) {
      return 'account-page';
    }
    if (path === '/') {
      return 'home-page';
    }
    return 'unknown-page';
  }, []);

  // 监听路由变化，自动将路径压入栈中，并设置当前页面
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // 设置当前页面 ID（用于返回键处理器管理）
    const pageId = getPageIdFromPath(currentPath);
    backHandlerManager.setCurrentPage(pageId);

    // 首次挂载时，初始化
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousPathRef.current = currentPath;

      // 首次加载时，如果不是首页，才将当前路径压入栈
      // 首页作为落地页不入栈，确保回退时能触发退出弹窗
      if (!isHomePage(currentPath)) {
        routeStackManager.push(currentPath);
      }

      return;
    }

    // 如果路径发生变化
    if (currentPath !== previousPath) {
      // 只有在非回退操作时才将当前路径压入栈
      // 同时，首页路由不入栈（作为落地页）
      console.log('[useBackNavigation] 路由变化，当前路径:', currentPath, '上一个路径:', previousPath);
      if (!isNavigatingBackRef.current && !isHomePage(currentPath)) {
        // 判断是否是频道页之间的切换（覆盖式替换）
        const isCurrentChannelPage = isChannelPage(currentPath);
        const isPreviousChannelPage = isChannelPage(previousPath);
        const shouldReplace = isCurrentChannelPage && isPreviousChannelPage;

        // 判断 play 路由之间是否是剧集切换，如果是则 shouldReplace 设为 true
        // /play/:cid/:vid 之间切换（同cid，vid变化）需确保 shouldReplace 为 true
        const currentPlayMatch = currentPath.match(/^\/play\/([^/]+)\/([^/]+)/);
        const previousPlayMatch = previousPath?.match(/^\/play\/([^/]+)(?:\/([^/]+))?$/);
        if (currentPlayMatch && previousPlayMatch) {
          // 只要当前和前一个都是 /play 页面，shouldReplace 必须是 true
          return;
        }

        // 特殊处理：进入播放页时，如果上一个页面不是播放详情页，则先插入播放详情页
        const isEnteringPlayPage = currentPath.startsWith('/play/');
        const stack = routeStackManager.getStack();
        const lastPathInStack = stack[stack.length - 1];

        if (isEnteringPlayPage && lastPathInStack && !lastPathInStack.startsWith('/playDetail/')) {
          // 从上一个页面不是播放详情页，插入播放详情页
          const cidMatch = currentPath.match(/^\/play\/([^/]+)/);
          if (cidMatch) {
            const cid = cidMatch[1];
            const playDetailPath = `/playDetail/${cid}?sourcePage=play`;
            console.log(`[useBackNavigation] 进入播放页，上一个页面不是播放详情页，插入: ${playDetailPath}`);
            routeStackManager.push(playDetailPath, false);
          }
        }

        routeStackManager.push(currentPath, shouldReplace);
      }

      // 重置回退标记
      isNavigatingBackRef.current = false;
      // 更新上一个路径的引用
      previousPathRef.current = currentPath;
    }
  }, [location.pathname, isHomePage, getPageIdFromPath, isChannelPage]);

  // 关闭浏览器窗口（兼容不同浏览器）
  const closeWebPage = useCallback(() => {
    if (navigator.userAgent.indexOf('MSIE') > 0) {
      // IE浏览器
      if (navigator.userAgent.indexOf('MSIE 6.0') > 0) {
        // IE6
        window.opener = null;
        window.close();
      } else {
        // IE6+
        window.open('', '_top');
        try {
          window.top?.close();
        } catch {
          // Ignore error
        }
      }
    } else {
      // 现代浏览器
      window.location.href = 'about:blank';
      window.opener = null;
      window.open('', '_self', '');
      window.close();
    }
  }, []);

  // 处理退出确认
  const handleExitConfirm = useCallback(() => {
    setShowExitDialog(false);

    // 延迟执行，确保弹窗关闭动画完成
    setTimeout(() => {
      closeWebPage();
    }, 200);
  }, [closeWebPage]);

  const handleExitCancel = useCallback(() => {
    setShowExitDialog(false);

    // 恢复焦点到打开弹窗前的位置
    if (lastFocusKeyRef.current) {
      setTimeout(() => {
        setFocus(lastFocusKeyRef.current as string);
        lastFocusKeyRef.current = null;
      }, 100);
    }
  }, []);

  // 处理回退逻辑
  const handleGoBack = useCallback(() => {
    const currentPath = location.pathname;
    const currentFocusKey = getCurrentFocusKey();

    // 优先检查是否有页面级的返回键处理器（栈顶）
    const pageId = getPageIdFromPath(currentPath);

    // 如果有页面级处理器，优先执行
    if (backHandlerManager.execute(pageId)) {
      console.log(`[useBackNavigation] 页面级处理器已处理返回键: ${pageId}`);
      return;
    }

    // 如果页面级处理器未处理，执行默认全局逻辑
    console.log('[useBackNavigation] 执行全局返回键逻辑');

    // 特殊处理：如果焦点在 Header 内的任何可聚焦元素上（导航栏、个人中心、VIP、搜索等）
    const isHeaderFocused =
      currentFocusKey?.startsWith('nav-') || currentFocusKey?.startsWith('header-') || currentFocusKey === 'header';

    // 历史记录目前可能没有焦点，并且聚焦有延时，可能会导致回退拉起退出弹窗
    const isHistoryPage = currentPath.startsWith('/history');
    if (isHeaderFocused && !isHistoryPage) {
      // 频道页在 Header 上按返回键，显示退出对话框
      if (isChannelPage(currentPath)) {
        lastFocusKeyRef.current = currentFocusKey;
        setShowExitDialog(true);
        return;
      }

      // 首页在 Header 上按返回键，显示退出对话框
      if (currentPath === '/') {
        lastFocusKeyRef.current = currentFocusKey;
        setShowExitDialog(true);
        return;
      }

      // 其他页面（如筛选页、历史页等）在 Header 上按返回键，也显示退出对话框
      // 统一行为：只要焦点在 Header 上，按返回就是退出，而不是路由回退
      lastFocusKeyRef.current = currentFocusKey;
      setShowExitDialog(true);
      return;
    }

    // 栈为空或只有一个元素时的特殊处理
    const stackLength = routeStackManager.getStack().length;
    if (stackLength <= 1) {
      // 如果是频道页，显示退出对话框
      if (isChannelPage(currentPath)) {
        lastFocusKeyRef.current = currentFocusKey || null;
        setShowExitDialog(true);
        return;
      }

      // 如果是根目录，显示退出对话框
      if (currentPath === '/') {
        lastFocusKeyRef.current = currentFocusKey || null;
        setShowExitDialog(true);
        return;
      }

      // 不是频道页也不是根目录，跳转到根目录
      isNavigatingBackRef.current = true;
      routeStackManager.clear(); // 清空栈
      navigate('/', { replace: true });
      return;
    }

    // 从栈中弹出当前路径，获取上一个路径
    const previousPath = routeStackManager.pop();

    if (previousPath) {
      // 标记为回退操作，避免路由变化监听时重复压栈
      isNavigatingBackRef.current = true;

      // 特殊处理：从播放页返回到播放详情页时，去掉 vid 参数
      let targetPath = previousPath;
      if (isPlayPage(currentPath) && isPlayDetailPage(previousPath)) {
        targetPath = removeVidFromPath(previousPath);
        console.log(`[useBackNavigation] 从播放页返回播放详情页，去掉vid: ${previousPath} -> ${targetPath}`);
      }

      // 导航到上一个路径
      navigate(targetPath);
    } else {
      // 无法回退，显示退出对话框
      lastFocusKeyRef.current = currentFocusKey || null;
      setShowExitDialog(true);
    }
  }, [navigate, location.pathname, isChannelPage, getPageIdFromPath]);

  // 处理返回键（检查是否需要取消弹窗）
  const handleBackKey = useCallback(
    (event?: KeyboardEvent) => {
      // 检查事件是否已被页面级处理
      if (event && isBackKeyHandled(event)) {
        console.log('[useBackNavigation] 返回键已被页面级处理，跳过全局处理');
        return;
      }

      // 🔑 关键优化：检查焦点是否在确认弹窗上
      // 如果焦点在弹窗上，说明弹窗的 onBackPress 应该已经处理了
      // 这里不再执行全局逻辑，避免焦点被意外移动
      const currentFocusKey = getCurrentFocusKey();
      if (currentFocusKey?.startsWith('confirm-dialog-')) {
        console.log('[useBackNavigation] 焦点在确认弹窗上，跳过全局处理（由弹窗自己处理）');
        return;
      }

      event?.preventDefault();

      // 如果退出对话框已显示，取消对话框
      if (showExitDialog) {
        handleExitCancel();
        return;
      }

      handleGoBack();
    },
    [showExitDialog, handleExitCancel, handleGoBack]
  );

  // 全局键盘监听
  useKeyboard({
    exit: handleBackKey,
    quit: handleBackKey,
  });

  return {
    goBack: handleGoBack,
    canGoBack: !routeStackManager.isAtBottom(),
    showExitDialog,
    onExitConfirm: handleExitConfirm,
    onExitCancel: handleExitCancel,
  };
};
